import { InjectionToken, Injectable, inject } from '@angular/core';
import { AiAssistantOwner } from '@cureskin/api-client-rn';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConnectionService } from '@services/connection-service';
import {
  AiAssistantChatState,
  AiAssistantMessage,
  AiAssistantPaginationState,
} from './ai-assistant-chat.types';

export const AI_CHAT_POLL_INTERVAL_MS = new InjectionToken<number>('AI_CHAT_POLL_INTERVAL_MS', {
  factory: (): number => 1000,
});
export const AI_CHAT_RETRY_DELAY_MS = new InjectionToken<number>('AI_CHAT_RETRY_DELAY_MS', {
  factory: (): number => 1000,
});

@Injectable()
export class AiAssistantChatService {
  private static readonly DEFAULT_PAGE_SIZE = 20;
  private static readonly MAX_RETRY_ATTEMPTS = 3;

  private readonly connectionService = inject(ConnectionService);
  private readonly pollSeedMs = inject(AI_CHAT_POLL_INTERVAL_MS);
  private readonly retryDelayMs = inject(AI_CHAT_RETRY_DELAY_MS);

  private readonly state = new BehaviorSubject<AiAssistantChatState>({
    messages: [],
    isThinking: false,
    latestMessage: null,
    isLoadingOldMessages: false,
  });

  private readonly pagination: AiAssistantPaginationState = {
    hasMore: true,
    pageSize: AiAssistantChatService.DEFAULT_PAGE_SIZE,
  };

  private pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private pollingIntervals = { interval1: this.pollSeedMs, interval2: this.pollSeedMs };
  private newestKnownTimestamp: string | null = null;
  private readonly seenMessageIds = new Set<string>();

  get showThinking$(): Observable<boolean> {
    return this.state.pipe(map(state => state.isThinking));
  }

  get allChats$(): Observable<AiAssistantMessage[]> {
    return this.state.pipe(map(state => state.messages));
  }

  get latestMessage$(): Observable<AiAssistantMessage | null> {
    return this.state.pipe(map(state => state.latestMessage));
  }

  get loadingOldMessages$(): Observable<boolean> {
    return this.state.pipe(map(state => state.isLoadingOldMessages));
  }

  get currentMessages(): AiAssistantMessage[] {
    return this.state.value.messages;
  }

  setThinking(value: boolean): void {
    this.updateState({ isThinking: value });
  }

  async loadChatHistory(): Promise<AiAssistantMessage[]> {
    this.pagination.hasMore = true;
    const fetched = await this.connectionService.listAiAssistantMessages(undefined, false, this.pagination.pageSize);
    this.pagination.hasMore = fetched.length >= this.pagination.pageSize;

    const messages = [...fetched].reverse();
    this.seenMessageIds.clear();
    messages.forEach(message => this.markSeen(message));
    this.newestKnownTimestamp = this.newestCreatedAt(messages) ?? this.newestKnownTimestamp;
    this.updateMessagesState(messages);
    return messages;
  }

  async loadMoreMessages(): Promise<boolean> {
    if (!this.pagination.hasMore || this.state.value.isLoadingOldMessages) {
      return false;
    }
    this.updateState({ isLoadingOldMessages: true });
    try {
      const oldestLoadedTimestamp = this.state.value.messages[0]?.createdAt;
      const fetched = await this.connectionService.listAiAssistantMessages(oldestLoadedTimestamp, false, this.pagination.pageSize);
      this.pagination.hasMore = fetched.length >= this.pagination.pageSize;

      const olderMessages = [...fetched].reverse().filter(message => this.markSeen(message));
      const merged = [...olderMessages, ...this.state.value.messages];
      this.updateState({
        messages: merged,
        latestMessage: merged.length ? merged[merged.length - 1] : null,
      });
      return this.pagination.hasMore;
    } finally {
      this.updateState({ isLoadingOldMessages: false });
    }
  }

  async sendMessage(text: string): Promise<AiAssistantMessage[]> {
    const message = text.trim();
    if (!message) return [];

    this.stopBotPolling();
    this.setThinking(true);
    this.addLocalMessages([this.createLocalUserMessage(message)]);

    const previousReply = this.lastBotMessage();
    try {
      const replies = await this.connectionService.sendAiAssistantMessage({
        message,
        state: previousReply?.state,
        turn: previousReply?.turn,
        interactionId: previousReply?.interactionId,
      });

      const userEcho = replies.find(reply => reply.owner === AiAssistantOwner.USER);
      const botReplies = replies.filter(reply => reply.owner !== AiAssistantOwner.USER);
      this.newestKnownTimestamp = this.newestCreatedAt(botReplies) ?? userEcho?.createdAt ?? this.newestKnownTimestamp;

      if (botReplies.length) {
        this.mergeServerMessages(botReplies);
        this.setThinking(false);
      } else {
        this.startBotPolling();
      }
      return replies;
    } catch (error) {
      this.setThinking(false);
      throw error;
    }
  }

  addLocalMessages(messages: AiAssistantMessage[]): void {
    if (!messages.length) return;
    const merged = [...this.state.value.messages, ...messages];
    this.updateState({ messages: merged, latestMessage: merged[merged.length - 1] });
  }

  replaceMessages(messages: AiAssistantMessage[]): void {
    this.updateMessagesState(messages);
  }

  resumePollingIfAwaitingReply(): void {
    const last = this.state.value.latestMessage;
    if (last?.owner === AiAssistantOwner.USER) {
      this.setThinking(true);
      this.startBotPolling();
    }
  }

  stopBotPolling(): void {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  private startBotPolling(): void {
    this.stopBotPolling();
    this.pollingIntervals = { interval1: this.pollSeedMs, interval2: this.pollSeedMs };
    this.scheduleNextPoll();
  }

  private scheduleNextPoll(): void {
    const wait = this.pollingIntervals.interval1 + this.pollingIntervals.interval2;
    this.pollingTimer = setTimeout(() => this.pollForBotReply(), wait);
  }

  private async pollForBotReply(retryCount = 0): Promise<void> {
    try {
      const fetched = await this.connectionService.listAiAssistantMessages(
        this.newestKnownTimestamp ?? undefined, true, this.pagination.pageSize);
      const added = this.mergeServerMessages(fetched.filter(message => message.owner !== AiAssistantOwner.USER));

      if (added.length) {
        this.stopBotPolling();
        this.setThinking(false);
        return;
      }
      this.pollingIntervals = {
        interval1: this.pollingIntervals.interval2,
        interval2: this.pollingIntervals.interval1 + this.pollingIntervals.interval2,
      };
      this.scheduleNextPoll();
    } catch {
      if (retryCount < AiAssistantChatService.MAX_RETRY_ATTEMPTS) {
        this.pollingTimer = setTimeout(() => this.pollForBotReply(retryCount + 1), this.retryDelayMs);
      } else {
        this.scheduleNextPoll();
      }
    }
  }

  private mergeServerMessages(messages: AiAssistantMessage[]): AiAssistantMessage[] {
    const unseenMessages = messages.filter(message => this.markSeen(message));
    if (!unseenMessages.length) return [];
    const merged = [...this.state.value.messages, ...unseenMessages];
    this.newestKnownTimestamp = this.newestCreatedAt(unseenMessages) ?? this.newestKnownTimestamp;
    this.updateState({ messages: merged, latestMessage: merged[merged.length - 1] });
    return unseenMessages;
  }

  private markSeen(message: AiAssistantMessage): boolean {
    const id = message.objectId;
    if (!id) return true;
    if (this.seenMessageIds.has(id)) return false;
    this.seenMessageIds.add(id);
    return true;
  }

  private newestCreatedAt(messages: AiAssistantMessage[]): string | null {
    return messages.reduce<string | null>((newest, message) =>
      (message.createdAt && (!newest || message.createdAt > newest) ? message.createdAt : newest), null);
  }

  private lastBotMessage(): AiAssistantMessage | null {
    return [...this.state.value.messages].reverse().find(message => message.owner === AiAssistantOwner.BOT) ?? null;
  }

  private createLocalUserMessage(content: string): AiAssistantMessage {
    return {
      objectId: `local_${crypto.randomUUID()}`,
      owner: AiAssistantOwner.USER,
      content,
      createdAt: new Date().toISOString(),
    };
  }

  private updateMessagesState(messages: AiAssistantMessage[]): void {
    this.updateState({
      messages,
      latestMessage: messages.length ? messages[messages.length - 1] : null,
    });
  }

  private updateState(partial: Partial<AiAssistantChatState>): void {
    this.state.next({ ...this.state.value, ...partial });
  }
}
